import { useCallback, useMemo } from 'react';

import { isGroupHeader } from '../helpers';
import {
  areGroupedItems,
  flattenGroupItemsToDownshift,
  flattenGroupItemsToNonDownshift,
  getStartingIndices,
} from '../helpers/groupedItems';
import isHiddenMenuItem from '../helpers/isHiddenMenuItem';
import { FlatGroupedItem, GenericItem, GroupHeader, ItemsProp } from '../types';

interface ProcessGroupHeaderConfig<TItem extends GenericItem> {
  groupHeader: GroupHeader;
  groupItemsWithIndices: Array<{ item: TItem; flatIndex: number }>;
  flatItemsWithGroupInfo: (FlatGroupedItem<TItem> | undefined)[] | undefined;
  allowCollapsibleGroups: boolean | undefined;
  expandedGroupKey: string | undefined;
}

interface ProcessGroupHeaderState<TItem extends GenericItem> {
  addedGroupHeaders: Set<GroupHeader>;
  addedItemsPerGroup: Map<string, Set<TItem>>;
  filteredGroupedMenuItems: (GroupHeader | TItem)[];
}

function isGroupExpanded(
  groupHeader: GroupHeader,
  allowCollapsibleGroups: boolean | undefined,
  expandedGroupKey: string | undefined,
): boolean {
  return !allowCollapsibleGroups || groupHeader.groupKey === expandedGroupKey;
}

function addGroupItemToResult<TItem extends GenericItem>(
  item: TItem,
  groupHeader: GroupHeader,
  filteredGroupedItem: FlatGroupedItem<TItem> | undefined,
  state: ProcessGroupHeaderState<TItem>,
): void {
  if (!state.addedItemsPerGroup.has(groupHeader.groupKey)) {
    state.addedItemsPerGroup.set(groupHeader.groupKey, new Set());
  }
  const groupAddedItems = state.addedItemsPerGroup.get(groupHeader.groupKey);
  if (!groupAddedItems || groupAddedItems.has(item)) {
    return;
  }
  state.filteredGroupedMenuItems.push(item);
  groupAddedItems.add(item);
  if (filteredGroupedItem) {
    filteredGroupedItem.index = state.filteredGroupedMenuItems.length - 1;
  }
}

function processGroupHeader<TItem extends GenericItem>(
  config: ProcessGroupHeaderConfig<TItem>,
  state: ProcessGroupHeaderState<TItem>,
): void {
  const { groupHeader, groupItemsWithIndices, flatItemsWithGroupInfo } = config;
  if (groupItemsWithIndices.length <= 0) {
    return;
  }
  if (!state.addedGroupHeaders.has(groupHeader)) {
    state.addedGroupHeaders.add(groupHeader);
    state.filteredGroupedMenuItems.push(groupHeader);
  }

  for (const { item, flatIndex } of groupItemsWithIndices) {
    const filteredGroupedItem = flatItemsWithGroupInfo?.[flatIndex];
    if (
      !isGroupExpanded(
        groupHeader,
        config.allowCollapsibleGroups,
        config.expandedGroupKey,
      )
    ) {
      continue;
    }
    addGroupItemToResult(item, groupHeader, filteredGroupedItem, state);
  }
}

function addItemToGroupIfNeeded<TItem extends GenericItem>(
  filteredItem: TItem,
  flatIndex: number,
  flatItemsWithGroupInfo: (FlatGroupedItem<TItem> | undefined)[] | undefined,
  itemsByGroup: Map<string, Array<{ item: TItem; flatIndex: number }>>,
  addedFlatIndicesByGroup: Map<string, Set<number>>,
): void {
  const filteredGroupedItem = flatItemsWithGroupInfo?.[flatIndex];
  if (!filteredGroupedItem?.groupKey) {
    return;
  }
  const groupKey = filteredGroupedItem.groupKey;
  if (!addedFlatIndicesByGroup.has(groupKey)) {
    addedFlatIndicesByGroup.set(groupKey, new Set());
  }
  const addedIndices = addedFlatIndicesByGroup.get(groupKey);
  if (!addedIndices || addedIndices.has(flatIndex)) {
    return;
  }
  addedIndices.add(flatIndex);
  if (!itemsByGroup.has(groupKey)) {
    itemsByGroup.set(groupKey, []);
  }
  itemsByGroup.get(groupKey)?.push({ item: filteredItem, flatIndex });
}

function buildItemsByGroup<TItem extends GenericItem>(
  filteredItemsToShow: TItem[],
  flatItems: TItem[],
  flatItemsWithGroupInfo: (FlatGroupedItem<TItem> | undefined)[] | undefined,
): Map<string, Array<{ item: TItem; flatIndex: number }>> {
  const itemsByGroup = new Map<
    string,
    Array<{ item: TItem; flatIndex: number }>
  >();
  const addedFlatIndicesByGroup = new Map<string, Set<number>>();

  // Build a Set of filtered items for O(1) membership checks
  const filteredSet = new Set<TItem>(filteredItemsToShow);

  // Iterate flatItems in original order so that per-group ordering is preserved
  // even when an item appears in both a pinned group and a regular group.
  for (let i = 0; i < flatItems.length; i++) {
    const item = flatItems[i];
    if (!filteredSet.has(item)) {
      continue;
    }
    addItemToGroupIfNeeded(
      item,
      i,
      flatItemsWithGroupInfo,
      itemsByGroup,
      addedFlatIndicesByGroup,
    );
  }
  return itemsByGroup;
}

function collectGroupHeadersFromFlatItems<TItem extends GenericItem>(
  flatItemsWithGroupHeaders:
    | (GroupHeader | FlatGroupedItem<TItem>)[]
    | undefined,
): {
  groupHeadersMap: Map<string, GroupHeader>;
  groupHeadersArray: (GroupHeader | FlatGroupedItem<TItem>)[];
} {
  const groupHeadersMap = new Map<string, GroupHeader>();
  const groupHeadersArray: (GroupHeader | FlatGroupedItem<TItem>)[] = [];
  if (!flatItemsWithGroupHeaders) {
    return { groupHeadersMap, groupHeadersArray };
  }
  for (const item of flatItemsWithGroupHeaders) {
    if (!item.isGroupHeader) {
      continue;
    }
    const header = item as GroupHeader;
    groupHeadersMap.set(header.groupKey, header);
    if (header.itemCount > 0) {
      groupHeadersArray.push(item);
    }
  }
  return { groupHeadersMap, groupHeadersArray };
}

const useGroupedItems = <TItem extends GenericItem>({
  maybeGroupedItems,
  allowCollapsibleGroups,
  expandedGroupKey,
  selectedGroupKey,
  showEmptyGroups,
}: {
  maybeGroupedItems: ItemsProp<TItem>;
  allowCollapsibleGroups?: boolean;
  expandedGroupKey?: string;
  selectedGroupKey?: string;
  showEmptyGroups?: boolean;
}) => {
  const isGrouped = useMemo(
    () => areGroupedItems(maybeGroupedItems),
    [maybeGroupedItems],
  );

  const flatItems = useMemo(
    () => flattenGroupItemsToDownshift(maybeGroupedItems),
    [maybeGroupedItems],
  );

  const visibleFlatItems = useMemo(
    () => flatItems.filter((item) => !isHiddenMenuItem(item)),
    [flatItems],
  );

  const { flatItemsWithGroupHeaders, flatItemsWithGroupInfo } = useMemo(() => {
    if (!areGroupedItems(maybeGroupedItems)) {
      return {
        startingIndices: new Map<number, number>(),
        indexGetter: undefined,
      };
    }

    const startingIndices = getStartingIndices(maybeGroupedItems);
    const flatItemsWithGroupHeaders = flattenGroupItemsToNonDownshift(
      maybeGroupedItems,
      startingIndices,
    );
    const flatItemsWithGroupInfo = flatItemsWithGroupHeaders.filter(
      (item) => !isGroupHeader(item),
    ) as FlatGroupedItem<TItem>[];

    return {
      flatItemsWithGroupHeaders,
      flatItemsWithGroupInfo,
    };
  }, [maybeGroupedItems]);

  const flatItemToIndex = useMemo(() => {
    const map = new Map<TItem, number[]>();
    flatItems.forEach((item, i) => {
      const indices = map.get(item);
      if (indices) {
        indices.push(i);
      } else {
        map.set(item, [i]);
      }
    });
    return map;
  }, [flatItems]);

  const groupHeadersData = useMemo(
    () => collectGroupHeadersFromFlatItems(flatItemsWithGroupHeaders),
    [flatItemsWithGroupHeaders],
  );

  /**
   * Index for downshift; skips group headers.
   * When an item appears at multiple flat indices (e.g. shared between pinned
   * and regular groups), uses indexHint to disambiguate which occurrence.
   */
  const indexGetter = useMemo(
    () =>
      isGrouped
        ? (item: TItem, indexHint?: number): number | undefined => {
            const flatIndices = flatItemToIndex.get(item);
            if (!flatIndices || flatIndices.length === 0) return undefined;

            if (flatIndices.length === 1) {
              return flatItemsWithGroupInfo?.[flatIndices[0]]?.index;
            }

            // Multiple occurrences — use indexHint (menu position, includes
            // headers) to pick the right one
            if (indexHint !== undefined && indexHint >= 0) {
              // Find the flat index whose menu position (grouped index) matches the hint
              for (const fi of flatIndices) {
                const groupedIndex = flatItemsWithGroupInfo?.[fi]?.index;
                if (groupedIndex === indexHint) {
                  return groupedIndex;
                }
              }
            }

            // Fallback to first occurrence
            return flatItemsWithGroupInfo?.[flatIndices[0]]?.index;
          }
        : null,
    [isGrouped, flatItemToIndex, flatItemsWithGroupInfo],
  );

  /**
   * Adds group headers for provided items.
   * @param itemsToShow - The (potentially) filtered list of items to show
   */
  const getMenuItems = useCallback(
    (itemsToShow: TItem[], countItems = true) => {
      if (!isGrouped) {
        return itemsToShow;
      }

      const filteredItemsToShow = itemsToShow.filter(
        (item) => !isHiddenMenuItem(item),
      );

      const { groupHeadersMap, groupHeadersArray } = groupHeadersData;

      if (filteredItemsToShow.length <= 0) {
        if (countItems) {
          groupHeadersMap.forEach((header) => {
            header.filteredItemCount = 0;
          });
        }
        return filteredItemsToShow;
      }

      const filteredGroupedMenuItems: (GroupHeader | TItem)[] = [];
      const addedItemsPerGroup = new Map<string, Set<TItem>>();
      const addedGroupHeaders = new Set<GroupHeader>();
      const itemsByGroup = buildItemsByGroup(
        filteredItemsToShow,
        flatItems,
        flatItemsWithGroupInfo,
      );
      if (countItems) {
        groupHeadersMap.forEach((header) => {
          header.filteredItemCount =
            itemsByGroup.get(header.groupKey)?.length ?? 0;
        });
      }
      const pinnedGroupHeaders = groupHeadersArray.filter(
        (header) => (header as GroupHeader).pinned,
      );
      const nonPinnedGroupHeaders = groupHeadersArray.filter(
        (header) => !(header as GroupHeader).pinned,
      );
      const processState: ProcessGroupHeaderState<TItem> = {
        addedGroupHeaders,
        addedItemsPerGroup,
        filteredGroupedMenuItems,
      };
      pinnedGroupHeaders.forEach((header) => {
        const groupHeader = header as GroupHeader;
        processGroupHeader(
          {
            groupHeader,
            groupItemsWithIndices: itemsByGroup.get(groupHeader.groupKey) || [],
            flatItemsWithGroupInfo,
            allowCollapsibleGroups,
            expandedGroupKey,
          },
          processState,
        );
      });
      const nonPinnedGroupsToProcess = selectedGroupKey
        ? nonPinnedGroupHeaders.filter(
            (header) => (header as GroupHeader).groupKey === selectedGroupKey,
          )
        : nonPinnedGroupHeaders;

      nonPinnedGroupsToProcess.forEach((header) => {
        const groupHeader = header as GroupHeader;
        processGroupHeader(
          {
            groupHeader,
            groupItemsWithIndices: itemsByGroup.get(groupHeader.groupKey) || [],
            flatItemsWithGroupInfo,
            allowCollapsibleGroups,
            expandedGroupKey,
          },
          processState,
        );
      });
      if (showEmptyGroups) {
        groupHeadersArray.forEach((item) => {
          if (!item.isGroupHeader) return;
          const groupHeader = item as GroupHeader;
          if (!addedGroupHeaders.has(groupHeader)) {
            addedGroupHeaders.add(groupHeader);
            filteredGroupedMenuItems.push(groupHeader);
          }
        });
      }

      return filteredGroupedMenuItems;
    },
    [
      allowCollapsibleGroups,
      expandedGroupKey,
      groupHeadersData,
      isGrouped,
      flatItems,
      flatItemsWithGroupInfo,
      selectedGroupKey,
      showEmptyGroups,
    ],
  );

  /**
   * All group headers for filter buttons (visible even when a category is selected).
   */
  const getAllGroupHeaders = useMemo(() => {
    if (
      !isGrouped ||
      !areGroupedItems(maybeGroupedItems) ||
      !flatItemsWithGroupHeaders
    ) {
      return [];
    }

    return flatItemsWithGroupHeaders
      .filter((item) => isGroupHeader(item))
      .map((item) => item as GroupHeader);
  }, [isGrouped, maybeGroupedItems, flatItemsWithGroupHeaders]);

  return {
    flatItems: visibleFlatItems,
    indexGetter,
    getMenuItems,
    isGrouped,
    getAllGroupHeaders,
  };
};
export default useGroupedItems;
